import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Typography,
    Button,
    List,
    Box,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Menu,
    MenuItem,
    Pagination,
    FormControl,
    InputLabel,
    Select,
    Grid,
    FormControlLabel,
    Checkbox,
    Card,
    CardContent,
    Collapse,
    Chip,
    IconButton
} from '@mui/material';
import { Delete, Add, FilterList, ExpandMore, ExpandLess, Translate, Edit } from '@mui/icons-material';
import { userRequest } from '../../requestMethods';
import ShortStoryForm from './ShortStoryForm';
import ShortStoryView from './ShortStoryView';
import ShortStoryListItem from './ShortStoryListItem';
import PageContainer from '../layout/PageContainer';

const ShortStoryList = () => {
    const navigate = useNavigate();
    const [stories, setStories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Search and filter states - load from localStorage or use defaults
    const [searchTerm, setSearchTerm] = useState(() => {
        const saved = localStorage.getItem('shortStoryList_searchTerm');
        return saved || '';
    });
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(() => {
        const saved = localStorage.getItem('shortStoryList_searchTerm');
        return saved || '';
    });
    const [languages, setLanguages] = useState([]);
    
    // Filter states - load from localStorage or use defaults
    const [filterExpanded, setFilterExpanded] = useState(false);
    const [hasThumbUrl, setHasThumbUrl] = useState(() => {
        const saved = localStorage.getItem('shortStoryList_hasThumbUrl');
        return saved === 'true';
    });
    const [isReadyToServe, setIsReadyToServe] = useState(() => {
        const saved = localStorage.getItem('shortStoryList_isReadyToServe');
        return saved === 'true';
    });
    
    // Pagination states - load from localStorage or use defaults
    const [currentPage, setCurrentPage] = useState(() => {
        const saved = localStorage.getItem('shortStoryList_currentPage');
        return saved ? parseInt(saved, 10) : 1;
    });
    const [pageSize, setPageSize] = useState(() => {
        const saved = localStorage.getItem('shortStoryList_pageSize');
        return saved ? parseInt(saved, 10) : 10;
    });
    const [totalPages, setTotalPages] = useState(0);
    const [totalStories, setTotalStories] = useState(0);
    
    // Dialog states
    const [formDialogOpen, setFormDialogOpen] = useState(false);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [selectedStory, setSelectedStory] = useState(null);

    // Menu states
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuStoryId, setMenuStoryId] = useState(null);

    // Debounce search term
    useEffect(() => {
        const handler = setTimeout(() => {
            const savedSearchTerm = localStorage.getItem('shortStoryList_searchTerm') || '';
            
            // Update debounced search term
            setDebouncedSearchTerm(searchTerm);
            
            // Only reset to page 1 if this is a user-initiated search change
            // (not when component mounts with saved search term)
            if (searchTerm !== savedSearchTerm) {
                setCurrentPage(1);
            }
        }, 500); // 500ms delay

        return () => {
            clearTimeout(handler);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]);

    useEffect(() => {
        fetchStories();
    }, [currentPage, pageSize, debouncedSearchTerm, hasThumbUrl, isReadyToServe]);

    // Fetch languages once when component mounts
    useEffect(() => {
        fetchLanguages();
    }, []);

    // Save pagination settings to localStorage whenever they change
    useEffect(() => {
        localStorage.setItem('shortStoryList_currentPage', currentPage.toString());
    }, [currentPage]);

    useEffect(() => {
        localStorage.setItem('shortStoryList_pageSize', pageSize.toString());
    }, [pageSize]);

    // Save search term to localStorage
    useEffect(() => {
        localStorage.setItem('shortStoryList_searchTerm', searchTerm);
    }, [searchTerm]);

    // Save filters to localStorage
    useEffect(() => {
        localStorage.setItem('shortStoryList_hasThumbUrl', hasThumbUrl.toString());
    }, [hasThumbUrl]);

    useEffect(() => {
        localStorage.setItem('shortStoryList_isReadyToServe', isReadyToServe.toString());
    }, [isReadyToServe]);

    const fetchStories = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page: currentPage.toString(),
                limit: pageSize.toString(),
                ...(debouncedSearchTerm.trim() && { search: debouncedSearchTerm.trim() }),
                ...(hasThumbUrl && { hasThumbUrl: 'true' }),
                ...(isReadyToServe && { isReadyToServe: 'true' })
            });
            
            const response = await userRequest.get(`/contentShortStory?${params}`);
            setStories(response.data.stories);
            setTotalPages(response.data.pagination.totalPages);
            setTotalStories(response.data.pagination.totalStories);
            
            // If current page is greater than total pages, reset to last page
            if (currentPage > response.data.pagination.totalPages && response.data.pagination.totalPages > 0) {
                setCurrentPage(response.data.pagination.totalPages);
            }
            
            setError('');
        } catch (error) {
            console.error('Error fetching stories:', error);
            setError('Failed to fetch stories');
        } finally {
            setLoading(false);
        }
    };

    const fetchLanguages = async () => {
        try {
            const response = await userRequest.get('/language');
            
            if (response.data && Array.isArray(response.data)) {
                setLanguages(response.data);
            } else {
                setLanguages([]);
            }
        } catch (error) {
            console.error('Error fetching languages:', error);
            setLanguages([]);
        }
    };

    const handleSearchChange = (event) => {
        const value = event.target.value;
        setSearchTerm(value);
        // Don't reset page here, let the debounced effect handle it
    };

    const handlePageSizeChange = (event) => {
        setPageSize(event.target.value);
        setCurrentPage(1); // Reset to first page when changing page size
    };

    const handleClearFilters = () => {
        setHasThumbUrl(false);
        setIsReadyToServe(false);
        setCurrentPage(1);
        // Clear from localStorage as well
        localStorage.setItem('shortStoryList_hasThumbUrl', 'false');
        localStorage.setItem('shortStoryList_isReadyToServe', 'false');
    };

    const handleCreateStory = () => {
        setEditMode(false);
        setSelectedStory(null);
        setFormDialogOpen(true);
    };

    const handleEditStory = (story) => {
        setEditMode(true);
        setSelectedStory(story);
        setFormDialogOpen(true);
    };

    const handleViewStory = (story) => {
        setSelectedStory(story);
        setViewDialogOpen(true);
    };

    const handleTranslateStory = (story) => {
        if (story.text?._id) {
            navigate(`/text/translate/${story.text._id}`);
        } else {
            setError('Cannot open translation editor: Text ID not found');
        }
    };

    const handleDeleteStory = async (storyId, storyTitle) => {
        const confirmed = window.confirm(
            `Are you sure you want to delete "${storyTitle}"? This will also delete the associated text and translations.`
        );
        
        if (!confirmed) return;

        try {
            await userRequest.delete(`/contentShortStory/${storyId}`);
            
            // If we're on the last page and this is the only item, go to previous page
            if (stories.length === 1 && currentPage > 1) {
                setCurrentPage(currentPage - 1);
            } else {
                // Refresh current page
                await fetchStories();
            }
        } catch (error) {
            console.error('Error deleting story:', error);
            setError('Failed to delete story');
        }
    };

    const handleFormSubmit = async (formData) => {
        try {
            if (editMode && selectedStory) {
                // Update existing story
                await userRequest.patch(`/contentShortStory/${selectedStory._id}`, formData);
            } else {
                // Create new story
                await userRequest.post('/contentShortStory', formData);
                // If creating a new story, go to first page to see it
                setCurrentPage(1);
            }
            
            // Refresh the current page data
            await fetchStories();
            setFormDialogOpen(false);
            setError('');
        } catch (error) {
            console.error('Error saving story:', error);
            setError('Failed to save story');
        }
    };

    const handleCloseForm = () => {
        setFormDialogOpen(false);
        setSelectedStory(null);
        setEditMode(false);
    };

    const handleCloseView = () => {
        setViewDialogOpen(false);
        setSelectedStory(null);
    };

    const handleMenuOpen = (event, story) => {
        setAnchorEl(event.currentTarget);
        setMenuStoryId(story._id);
        setSelectedStory(story); // Set the story for menu actions
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuStoryId(null);
        setSelectedStory(null);
    };

    const handleMenuDelete = () => {
        if (menuStoryId && selectedStory) {
            handleDeleteStory(menuStoryId, selectedStory.title);
            handleMenuClose();
        }
    };

    if (loading) {
        return (
            <PageContainer>
                <Typography variant="h4">Loading...</Typography>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">Short Stories</Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Add />}
                    onClick={handleCreateStory}
                >
                    Add New Story
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <TextField
                fullWidth
                label="Search stories..."
                variant="outlined"
                value={searchTerm}
                onChange={handleSearchChange}
                sx={{ mb: 2 }}
                placeholder="Search by title, author, or tags"
            />

            {/* Filter Section */}
            <Card sx={{ mb: 3 }}>
                <CardContent sx={{ pb: filterExpanded ? 2 : 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={1}>
                            <FilterList />
                            <Typography variant="h6">Filters</Typography>
                            {(hasThumbUrl || isReadyToServe) && (
                                <Chip 
                                    label={`${(hasThumbUrl ? 1 : 0) + (isReadyToServe ? 1 : 0)} active`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                />
                            )}
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                            {(hasThumbUrl || isReadyToServe) && (
                                <Button 
                                    size="small" 
                                    onClick={handleClearFilters}
                                    variant="outlined"
                                >
                                    Clear All
                                </Button>
                            )}
                            <IconButton onClick={() => setFilterExpanded(!filterExpanded)}>
                                {filterExpanded ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                        </Box>
                    </Box>
                    
                    <Collapse in={filterExpanded}>
                        <Box sx={{ mt: 2 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={hasThumbUrl}
                                                onChange={(e) => {
                                                    setHasThumbUrl(e.target.checked);
                                                    setCurrentPage(1);
                                                }}
                                            />
                                        }
                                        label="Has Thumbnail Image"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={isReadyToServe}
                                                onChange={(e) => {
                                                    setIsReadyToServe(e.target.checked);
                                                    setCurrentPage(1);
                                                }}
                                            />
                                        }
                                        label="Ready to Serve"
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </Collapse>
                </CardContent>
            </Card>

            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">
                    {totalStories} {totalStories === 1 ? 'story' : 'stories'} found
                </Typography>
                
                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Items per page</InputLabel>
                    <Select
                        value={pageSize}
                        label="Items per page"
                        onChange={handlePageSizeChange}
                    >
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            <List>
                {stories.map((story, index) => {
                    const orderNumber = (currentPage - 1) * pageSize + index + 1;
                    return (
                        <ShortStoryListItem
                            key={story._id}
                            story={story}
                            orderNumber={orderNumber}
                            onView={handleViewStory}
                            onEdit={handleEditStory}
                            onTranslate={handleTranslateStory}
                            onMenuOpen={handleMenuOpen}
                        />
                    );
                })}
            </List>

            {stories.length === 0 && !loading && (
                <Box textAlign="center" py={4}>
                    <Typography variant="h6" color="text.secondary">
                        No stories found
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<Add />}
                        onClick={handleCreateStory}
                        sx={{ mt: 2 }}
                    >
                        Create Your First Story
                    </Button>
                </Box>
            )}

            {/* Form Dialog */}
            <Dialog open={formDialogOpen} onClose={handleCloseForm} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editMode ? 'Edit Short Story' : 'Create New Short Story'}
                </DialogTitle>
                <DialogContent>
                    <ShortStoryForm
                        story={selectedStory}
                        languages={languages}
                        onSubmit={handleFormSubmit}
                        onCancel={handleCloseForm}
                    />
                </DialogContent>
            </Dialog>

            {/* View Dialog */}
            <Dialog open={viewDialogOpen} onClose={handleCloseView} maxWidth="lg" fullWidth>
                <DialogTitle>
                    {selectedStory?.title}
                </DialogTitle>
                <DialogContent>
                    <ShortStoryView story={selectedStory} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseView}>Close</Button>
                    {selectedStory && (
                        <>
                            <Button 
                                onClick={() => handleTranslateStory(selectedStory)} 
                                startIcon={<Translate />}
                                color="primary"
                            >
                                Translate
                            </Button>
                            <Button onClick={() => handleEditStory(selectedStory)} startIcon={<Edit />}>
                                Edit
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            {/* Menu Dialog */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem onClick={handleMenuDelete} sx={{ color: 'error.main' }}>
                    <Delete sx={{ mr: 1 }} />
                    Delete Story
                </MenuItem>
            </Menu>

            <Grid container justifyContent="center" sx={{ mt: 3 }}>
                {totalPages > 1 && (
                    <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                        <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={(event, value) => {
                                setCurrentPage(value);
                            }}
                            color="primary"
                            size="large"
                            showFirstButton
                            showLastButton
                        />
                        <Typography variant="body2" color="text.secondary">
                            Page {currentPage} of {totalPages} 
                            ({Math.min((currentPage - 1) * pageSize + 1, totalStories)}-{Math.min(currentPage * pageSize, totalStories)} of {totalStories} stories)
                        </Typography>
                    </Box>
                )}
            </Grid>
        </PageContainer>
    );
};

export default ShortStoryList; 